import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputEmail } from '../InputEmail';
import { InputPassword } from '../InputPassword';
import { ErrorNotice } from '../common/ErrorNotice';
import {
  PASSWORD_ALLOWED_CHARS_REGEX,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from '@/constants/validation';
import { useSuiteLoungeFlow } from '@/hooks/useSuiteLoungeFlow';

interface SignUpFormData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  agreeToANATerms: boolean;
  agreeToBitkeyTerms: boolean;
}

interface SignUpFormProps {
  onSubmit: (data: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  }) => void;
  isSubmitting: boolean;
  error?: {
    message: string;
    detailMessages?: string[];
  } | null;
}

export const SignUpForm = ({
  onSubmit,
  isSubmitting,
  error,
}: SignUpFormProps) => {
  const { t } = useTranslation();
  const { isSuiteLoungeFlow } = useSuiteLoungeFlow();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      agreeToANATerms: false,
      agreeToBitkeyTerms: false,
    },
  });

  const password = watch('password');
  const agreeToANATerms = watch('agreeToANATerms');
  const agreeToBitkeyTerms = watch('agreeToBitkeyTerms');

  const onFormSubmit = (data: SignUpFormData) => {
    onSubmit({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      password: data.password,
    });
  };

  const isFormValid = agreeToANATerms && agreeToBitkeyTerms && !isSubmitting;

  return (
    <section className="l-card">
      <h2 className="l-card__title">{t('signup.title')}</h2>

      <div className="c-auth c-auth--login">
        <form className="c-auth__form" onSubmit={handleSubmit(onFormSubmit)}>
          <div className="c-auth__fields">
            {/* メールアドレス */}
            <div className="c-auth__field">
              <label className="c-auth__label">
                <span className="c-auth__label-text">{t('signup.email')}</span>
                <InputEmail
                  className="c-auth__input"
                  placeholder={t('signup.emailPlaceholder')}
                  {...register('email', {
                    required: t('signup.validation.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('signup.validation.emailInvalid'),
                    },
                  })}
                />
              </label>
            </div>
            {errors.email && (
              <div className="common-error">{errors.email.message}</div>
            )}
            {/* お名前 */}
            <div className="c-auth__field c-auth__field--name">
              <div className="c-auth__name">
                <label className="c-auth__label c-auth__label--family">
                  <span className="c-auth__label-text">
                    {t('signup.lastName')}
                  </span>
                  <input
                    className="c-auth__input c-auth__input--family"
                    type="text"
                    placeholder={t('signup.lastNamePlaceholder')}
                    {...register('lastName', {
                      required: t('signup.validation.lastNameRequired'),
                    })}
                  />
                </label>
                <label className="c-auth__label c-auth__label--given">
                  <span className="c-auth__label-text">
                    {t('signup.firstName')}
                  </span>
                  <input
                    className="c-auth__input c-auth__input--given"
                    type="text"
                    placeholder={t('signup.firstNamePlaceholder')}
                    {...register('firstName', {
                      required: t('signup.validation.firstNameRequired'),
                    })}
                  />
                </label>
              </div>
              {errors.lastName && (
                <div className="common-error">{errors.lastName.message}</div>
              )}
              {errors.firstName && (
                <div className="common-error">{errors.firstName.message}</div>
              )}
            </div>
          </div>

          <h2 className="l-card__title">{t('signup.passwordSetup')}</h2>
          <p className="l-text l-text--white l-text--left l-text--spacing">
            {t('signup.passwordRequirements')}
          </p>
          <div className="c-auth__fields">
            {/* パスワード */}
            <div className="c-auth__field">
              <label className="c-auth__label">
                <span className="c-auth__label-text">
                  {t('signup.password')}
                </span>
                <InputPassword
                  className="c-auth__input"
                  placeholder={t('signup.passwordPlaceholder')}
                  {...register('password', {
                    required: t('signup.validation.passwordRequired'),
                    minLength: {
                      value: PASSWORD_MIN_LENGTH,
                      message: t('signup.validation.passwordMinLength'),
                    },
                    maxLength: {
                      value: PASSWORD_MAX_LENGTH,
                      message: t('signup.validation.passwordMaxLength'),
                    },
                    pattern: {
                      value: PASSWORD_ALLOWED_CHARS_REGEX,
                      message: t('signup.validation.passwordInvalidChars'),
                    },
                  })}
                />
              </label>
              {errors.password && (
                <div className="common-error">{errors.password.message}</div>
              )}
            </div>

            {/* パスワード確認 */}
            <div className="c-auth__field">
              <label className="c-auth__label">
                <span className="c-auth__label-text">
                  {t('signup.confirmPassword')}
                </span>
                <InputPassword
                  className="c-auth__input"
                  placeholder={t('signup.passwordPlaceholder')}
                  {...register('confirmPassword', {
                    required: t('signup.validation.confirmPasswordRequired'),
                    validate: (value) =>
                      value === password ||
                      t('signup.validation.passwordMismatch'),
                  })}
                />
              </label>
              {errors.confirmPassword && (
                <div className="common-error">
                  {errors.confirmPassword.message}
                </div>
              )}
            </div>
          </div>

          {/* 同意チェック群 */}
          <div className="password-change">
            <label className="password-change__confirm">
              <input
                type="checkbox"
                className="password-change__confirm-checkbox"
                {...register('agreeToANATerms', {
                  required: t('signup.validation.agreeToANATermsRequired'),
                })}
              />
              <span className="password-change__confirm-label">
                {t('signup.agreeToANATermsPrefix')}
                {isSuiteLoungeFlow ? (
                  <a
                    href={t(
                      'common.links.loungeReservation.showerAndPrivateRoomTermsOfUse',
                    )}
                    target="_blank"
                    className="password-change__link password-change__link--terms-ana"
                    rel="noopener noreferrer"
                  >
                    {t('signup.showerAndPrivateRoomTermsOfUse')}
                  </a>
                ) : (
                  <a
                    href={t(
                      'common.links.loungeReservation.showerRoomTermsOfUse',
                    )}
                    target="_blank"
                    className="password-change__link password-change__link--terms-ana"
                    rel="noopener noreferrer"
                  >
                    {t('signup.showerRoomTermsOfUse')}
                  </a>
                )}
                {t('signup.and')}
                <a
                  href={t('common.links.loungeReservation.privacyPolicy')}
                  target="_blank"
                  className="password-change__link password-change__link--privacy-ana"
                  rel="noopener noreferrer"
                >
                  {t('signup.privacyPolicy')}
                </a>
                {t('signup.agreeToANATermsSuffix')}
              </span>
            </label>
            {errors.agreeToANATerms && (
              <div className="common-error">
                {errors.agreeToANATerms.message}
              </div>
            )}

            <label className="password-change__confirm">
              <input
                type="checkbox"
                className="password-change__confirm-checkbox"
                {...register('agreeToBitkeyTerms', {
                  required: t('signup.validation.agreeToBitkeyTermsRequired'),
                })}
              />
              <span className="password-change__confirm-label">
                {t('signup.agreeToBitkeyTermsPrefix')}
                <a
                  href={t('common.links.workhub.termsOfUse')}
                  target="_blank"
                  className="password-change__link password-change__link--terms-workhub"
                  rel="noopener noreferrer"
                >
                  {t('signup.termsOfUse')}
                </a>
                {t('signup.and')}
                <a
                  href={t('common.links.workhub.privacyPolicy')}
                  target="_blank"
                  className="password-change__link password-change__link--privacy-workhub"
                  rel="noopener noreferrer"
                >
                  {t('signup.privacyPolicy')}
                </a>
                {t('signup.agreeToBitkeyTermsSuffix')}
              </span>
            </label>
            {errors.agreeToBitkeyTerms && (
              <div className="common-error">
                {errors.agreeToBitkeyTerms.message}
              </div>
            )}

            {error && (
              <div className="c-auth__alerts">
                <ErrorNotice
                  message={error.message}
                  details={error.detailMessages}
                />
              </div>
            )}

            <button
              className={`password-change__submit c-auth__btn c-auth__btn--next ${!isFormValid ? 'is-disabled' : ''}`}
              type="submit"
              disabled={!isFormValid}
            >
              {isSubmitting
                ? t('signup.createAccountButtonLoading')
                : t('signup.createAccountButton')}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
